import { formatINR } from '@/utils/format';

type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';

export interface ItemDetailData {
  id: string;
  name: string;
  code?: string;
  barcode?: string;
  categoryName?: string;
  unitName?: string;
  brand?: string;
  articleNo?: string;
  hsnCode?: string;
  taxRate?: number;
  purchaseRate: number;
  saleRate: number;
  mrp?: number;
  stock: number;
  reservedQty?: number;
  availableQty?: number;
  minStockLevel?: number;
  isActive: boolean;
  sizeColor?: string;
  itemType?: string;
  procurementType?: string;
  warehouseName?: string;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  hasExpiry?: boolean;
  shelfLifeDays?: number | null;
  reorderPoint?: number;
  reorderQty?: number;
  leadTimeDays?: number;
  standardCost?: number;
  bomId?: string | null;
  bomVersion?: string | null;
  drawingNumber?: string | null;
  specifications?: string | null;
  requiresIncomingQC?: boolean;
  requiresFinalQC?: boolean;
  isParent?: boolean;
  hasVariants?: boolean;
  preferredSupplierName?: string | null;
  preferredSubcontractorName?: string | null;
  jobWorkRate?: number;
  scrapRate?: number;
  bomCode?: string | null;
  productionUnit?: string | null;
  itemGroup?: string | null;
  serialPrefix?: string | null;
  defaultWarrantyMonths?: number | null;
  variantCount?: number;
  isVariant?: boolean;
  variantName?: string | null;
  variantSku?: string | null;
}

interface ItemDetailProps {
  item: ItemDetailData | null;
  onClose: () => void;
  onOpenReservations?: (item: ItemDetailData) => void;
}

function getStockStatus(item: ItemDetailData): StockStatus {
  const stock = Number(item.stock ?? 0);
  const minStockLevel = Number(item.minStockLevel ?? 0);
  if (stock === 0) return 'OUT_OF_STOCK';
  if (stock < minStockLevel) return 'LOW_STOCK';
  return 'IN_STOCK';
}

function stockBadge(item: ItemDetailData) {
  const status = getStockStatus(item);
  if (status === 'IN_STOCK') return { cls: 'bg-green-100 text-green-700', label: 'In Stock' };
  if (status === 'LOW_STOCK') return { cls: 'bg-amber-100 text-amber-700', label: 'Low Stock' };
  return { cls: 'bg-red-100 text-red-600', label: 'Out of Stock' };
}

const PROCUREMENT_LABELS: Record<string, string> = { MAKE: 'Make', BUY: 'Buy', SUBCONTRACT: 'Subcontract' };

const ITEM_TYPE_LABELS: Record<string, string> = {
  RAW_MATERIAL: 'Raw Material',
  SEMI_FINISHED: 'Semi-Finished',
  FINISHED_GOOD: 'Finished Good',
  CONSUMABLE: 'Consumable',
  PACKAGING: 'Packaging',
};

function InfoBlock({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5">
      <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#94a3b8]">{label}</p>
      <p className={`mt-0.5 text-[11px] font-medium text-[#0f172a] truncate ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

function MetricBlock({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className={`rounded border px-2 py-1.5 ${accent ? 'border-indigo-200 bg-indigo-50' : 'border-[#e2e8f0] bg-[#f8fafc]'}`}>
      <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#94a3b8]">{label}</p>
      <p className={`mt-0.5 text-[11px] font-bold truncate ${accent ? 'text-[#4f46e5]' : 'text-[#0f172a]'}`}>{value}</p>
    </div>
  );
}

export default function ItemDetail({
  item,
  onClose,
  onOpenReservations,
}: ItemDetailProps) {
  if (!item) return null;

  const badge = stockBadge(item);
  const typeLabel = item.itemType ? (ITEM_TYPE_LABELS[item.itemType] || item.itemType) : '—';

  return (
    <div className="fixed top-[120px] right-6 z-40 w-[430px] max-h-[calc(100vh-140px)] flex flex-col rounded-2xl border border-[#dbe4f0] bg-white shadow-[0_20px_60px_rgba(15,23,42,0.18)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3.5 py-3 bg-[#f8fafc] border-b border-[#e2e8f0] shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#64748b]">Item Info</p>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                item.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
              }`}
            >
              {item.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <h3 className="mt-0.5 text-sm font-bold text-[#0f172a] truncate">{item.name}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}>
            {badge.label}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-200 text-[#64748b] transition-colors cursor-pointer"
            aria-label="Close item information"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      </div>

      {/* Content Body */}
      <div className="px-3.5 py-3 space-y-3 flex-1 overflow-y-auto">
        {/* Identification & Classification */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b] mb-1.5 px-0.5">
            Identification & Classification
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            <InfoBlock label="Code / SKU" value={item.code || item.variantSku || '—'} mono />
            <InfoBlock label="Barcode" value={item.barcode || '—'} mono />
            <InfoBlock label="Category" value={item.categoryName || '—'} />
            <InfoBlock label="Brand" value={item.brand || '—'} />
            <InfoBlock label="Article No" value={item.articleNo || '—'} mono />
            <InfoBlock label="Unit" value={item.unitName || '—'} />
            <InfoBlock label="Item Type" value={typeLabel} />
            <InfoBlock label="Procurement" value={PROCUREMENT_LABELS[item.procurementType || ''] || '—'} />
            <InfoBlock label="Warehouse" value={item.warehouseName || '—'} />
            <InfoBlock label="Size / Color" value={item.sizeColor || '—'} />
          </div>
        </div>

        {/* Tax & Pricing */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b] mb-1.5 px-0.5">
            Tax & Pricing
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            <InfoBlock label="HSN Code" value={item.hsnCode || '—'} mono />
            <InfoBlock label="GST Rate" value={`${item.taxRate ?? 0}%`} />
          </div>
          <div className="grid grid-cols-4 gap-1.5 mt-1.5">
            <MetricBlock label="Purchase" value={item.purchaseRate && item.purchaseRate > 0 ? formatINR(item.purchaseRate) : '—'} />
            <MetricBlock label="Sale Price" value={item.saleRate && item.saleRate > 0 ? formatINR(item.saleRate) : '—'} accent />
            <MetricBlock label="MRP" value={item.mrp && item.mrp > 0 ? formatINR(item.mrp) : '—'} />
            <MetricBlock label="Std. Cost" value={item.standardCost && item.standardCost > 0 ? formatINR(item.standardCost) : '—'} />
          </div>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            <InfoBlock label="Scrap Value / unit" value={item.scrapRate && item.scrapRate > 0 ? `₹${item.scrapRate}` : '—'} />
            <InfoBlock label="Job Work Charge / unit" value={item.jobWorkRate && item.jobWorkRate > 0 ? `₹${item.jobWorkRate}` : '—'} />
          </div>
        </div>

        {/* Inventory & Replenishment */}
        <div>
          <div className="flex items-center justify-between mb-1.5 px-0.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b]">
              Inventory & Availability
            </p>
            {onOpenReservations && (
              <button
                type="button"
                onClick={() => onOpenReservations(item)}
                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer transition-colors"
              >
                <i className="ri-lock-line" /> View Reservations
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <MetricBlock label="Total Stock" value={`${item.stock ?? 0}`} />
            <div
              onClick={() => onOpenReservations?.(item)}
              role="button"
              tabIndex={0}
              className="rounded border border-amber-200 bg-amber-50/70 px-2 py-1.5 cursor-pointer hover:bg-amber-100/70 transition-colors"
              title="Click to view reservations"
            >
              <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-amber-700 flex items-center gap-0.5">
                <i className="ri-lock-line" /> Reserved
              </p>
              <p className="mt-0.5 text-[11px] font-bold text-amber-900 truncate">
                {item.reservedQty ?? 0}
              </p>
            </div>
            <MetricBlock
              label="Available"
              value={`${item.availableQty ?? Math.max(0, (item.stock ?? 0) - (item.reservedQty ?? 0))}`}
              accent
            />
          </div>
          <div className="grid grid-cols-4 gap-1.5 mt-1.5">
            <MetricBlock label="Min Level" value={item.minStockLevel && item.minStockLevel > 0 ? `${item.minStockLevel}` : '—'} />
            <MetricBlock label="Reorder Pt." value={item.reorderPoint && item.reorderPoint > 0 ? `${item.reorderPoint}` : '—'} />
            <MetricBlock label="Reorder Qty" value={item.reorderQty && item.reorderQty > 0 ? `${item.reorderQty}` : '—'} />
            <InfoBlock label="Lead Time" value={item.leadTimeDays && item.leadTimeDays > 0 ? `${item.leadTimeDays} Days` : '—'} />
          </div>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            <InfoBlock label="Preferred Supplier" value={item.preferredSupplierName || '—'} />
            <InfoBlock label="Preferred Sub-contractor" value={item.preferredSubcontractorName || '—'} />
          </div>
        </div>

        {/* Tracking & Quality Controls */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b] mb-1.5 px-0.5">
            Tracking & Controls
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            <InfoBlock label="Batch Tracked" value={item.isBatchTracked ? 'Yes' : 'No'} />
            <InfoBlock
              label="Serial Tracked"
              value={item.isSerialTracked
                ? ['Yes', item.serialPrefix && `prefix ${item.serialPrefix}`, item.defaultWarrantyMonths && `${item.defaultWarrantyMonths}m warranty`].filter(Boolean).join(' · ')
                : 'No'}
            />
            <InfoBlock
              label="Expiry Control"
              value={item.hasExpiry ? `Yes (${item.shelfLifeDays ?? 0} days)` : 'No'}
            />
            <InfoBlock
              label="Quality Control"
              value={
                item.requiresIncomingQC || item.requiresFinalQC
                  ? [item.requiresIncomingQC && 'Incoming', item.requiresFinalQC && 'Final']
                      .filter(Boolean)
                      .join(' + ')
                  : 'None'
              }
            />
          </div>
        </div>

        {/* Manufacturing & Details */}
        {(item.bomId || item.drawingNumber || item.specifications || item.productionUnit || item.itemGroup) && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b] mb-1.5 px-0.5">
              Manufacturing Details
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {item.bomId && <InfoBlock label="BOM / Ver." value={`${item.bomCode || item.bomId}${item.bomVersion ? ` (v${item.bomVersion})` : ''}`} mono />}
              {item.drawingNumber && <InfoBlock label="Drawing No." value={item.drawingNumber} mono />}
              {item.productionUnit && <InfoBlock label="Production Unit" value={item.productionUnit} />}
              {item.itemGroup && <InfoBlock label="Item Group" value={item.itemGroup} />}
            </div>
            {item.specifications && (
              <div className="mt-1.5">
                <InfoBlock label="Specifications" value={item.specifications} />
              </div>
            )}
          </div>
        )}

        {/* Variants Info */}
        {(item.isParent || item.hasVariants || item.isVariant) && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#64748b] mb-1.5 px-0.5">
              Variants Information
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              <InfoBlock
                label="Variant Status"
                value={item.isVariant ? 'Child Variant' : 'Parent Product'}
              />
              <InfoBlock
                label="Total Variants"
                value={item.variantCount !== undefined ? `${item.variantCount}` : '—'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}